
import { Component } from "vue-property-decorator";
import { ProjectView } from "@/views/project-view";
import { ObjectHelper, QuickHelper } from "@root/core";
import { extractErrorCode, resolveErrorMessageKey } from "@root/core/utilities";
import { promoRedeemErrorMessageMap, promoRedeemFallbackKey } from "@/helpers/error-code";
import TranslationJobHeaderControls from "@/views/AdminPages/TranslationJob/components/translation-job-header-controls.vue";
import TranslationJobTopupModal from "@/views/AdminPages/TranslationJob/components/translation-job-topup-modal.vue";

type OrganizationOption = {
    id: string;
    name: string;
    userId?: string | null;
    wallet?: {
        id?: string | null;
        balance?: number | null;
    } | null;
    memberships?: Array<{
        role?: string | null;
    }>;
};

@Component({
    components: {
        "ivc-translation-job-header-controls": TranslationJobHeaderControls,
        "ivc-translation-job-topup-modal": TranslationJobTopupModal,
    },
})
export default class extends ProjectView {
    /// base
    quick = this.quicks().Ledger;

    organizations: OrganizationOption[] = [];
    currentOrgId: string | null = null;
    currentUserId: string | null = null;

    sourceFilter = "all";
    showTopupModal = false;
    topupTargetName = "-";
    isRedeeming = false;

    get ObjectHelper() {
        return ObjectHelper;
    }
    get QuickHelper() {
        return QuickHelper;
    }

    get currentOrg() {
        return this.organizations.find((org) => org.id === this.currentOrgId) ?? null;
    }

    get currentBalance() {
        return this.currentOrg?.wallet?.balance ?? 0;
    }

    get currentWalletId() {
        return this.currentOrg?.wallet?.id ?? null;
    }

    get tableParams() {
        const where: Record<string, any> = {};
        if (this.currentWalletId) {
            where.walletId = this.currentWalletId;
        }
        if (this.sourceFilter !== "all") {
            where.source = this.sourceFilter;
        }
        return { where };
    }

    async mounted() {
        await this.loadOrganizations();
    }

    selectOrganization(id: string) {
        if (this.currentOrgId === id) return;
        this.currentOrgId = id;
        this.reloadLedgerTable();
    }

    private reloadLedgerTable() {
        const tableRef = this.ref("ledger/R").ref as any;
        tableRef?.reload?.();
    }

    async loadOrganizations() {
        const userId = this.resolveUserId();
        if (!userId) return;
        this.currentUserId = userId;

        const body = {
            where: {
                OR: [
                    { userId: { equals: userId } },
                    { memberships: { some: { userId: { equals: userId } } } },
                ],
            },
            include: {
                wallet: true,
                memberships: {
                    where: { userId: { equals: userId } },
                    select: { role: true },
                },
            },
            orderBy: [{ createdAt: "asc" as const }],
        };

        try {
            const payload = await this.$server.R("organization", body);
            const results = payload?.results ?? payload ?? [];
            this.organizations = Array.isArray(results) ? results : [];
        } catch (error) {
            this.console.error("Load organizations failed", error);
            return;
        }

        if (!this.currentOrgId && this.organizations.length > 0) {
            this.currentOrgId = this.organizations[0].id;
            this.reloadLedgerTable();
        }
    }

    openTopupModal(payload?: { name?: string }) {
        const fallbackName = this.currentOrg?.name ?? "-";
        this.topupTargetName = payload?.name ?? fallbackName;
        this.showTopupModal = true;
    }

    async redeemTopupCode(payload: { code?: string }) {
        if (this.isRedeeming) return;
        const code = payload?.code?.trim() ?? "";
        if (!code) {
            this.$alert?.(
                {
                    title: this.$_("w_Error"),
                    message: this.$_("w_TranslationJobTopupRedeemEmpty"),
                },
                "danger",
            );
            return;
        }

        this.isRedeeming = true;
        try {
            const result = await this.$server.C("promotion-code/redeem", {
                code,
                organizationId: this.currentOrgId ?? undefined,
            });
            const creditApplied = Number(result?.creditApplied ?? 0);
            this.$alert?.(
                {
                    title: this.$_("w_Success"),
                    message: this.$_("w_TranslationJobTopupRedeemSuccess", [
                        creditApplied.toFixed(2),
                    ]),
                },
                "success",
            );
            await this.loadOrganizations();
            this.reloadLedgerTable();
            this.showTopupModal = false;
        } catch (error) {
            if ((error as any)?.handled) return;
            const code = extractErrorCode((error as any)?.body ?? error) ?? "";
            const messageKey = resolveErrorMessageKey(
                code,
                promoRedeemErrorMessageMap,
                promoRedeemFallbackKey,
            );
            this.$alert?.(
                {
                    title: this.$_("w_Error"),
                    message: this.$_(messageKey),
                },
                "danger",
            );
            (error as any).handled = true;
        } finally {
            this.isRedeeming = false;
        }
    }

    async handleTopupSucceed(payload?: { amount?: number }) {
        if (payload?.amount && this.currentOrg?.wallet) {
            this.currentOrg.wallet.balance =
                (this.currentOrg.wallet.balance ?? 0) + Number(payload.amount);
        }
        await this.loadOrganizations();
        this.reloadLedgerTable();
        this.showTopupModal = false;
    }

    resolveUserId() {
        const token = this.resolveAccessToken();
        if (!token) return null;
        const payload = this.decodeJwtPayload(token);
        return payload?.sub ?? null;
    }

    private resolveAccessToken() {
        const headers = this.$server.applyAccessToken?.() ?? {};
        const rawAuth = headers.Authorization ?? "";
        const token = String(rawAuth).replace(/^Bearer\s+/i, "");
        return token || null;
    }

    decodeJwtPayload(token: string) {
        const parts = token.split(".");
        if (parts.length < 2) return null;
        try {
            const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
            const padded = base64.padEnd(
                base64.length + ((4 - (base64.length % 4)) % 4),
                "=",
            );
            const decoded = atob(padded);
            return JSON.parse(decoded);
        } catch (error) {
            this.console.warn("Failed to decode token", error);
            return null;
        }
    }
}
